import React, { useState } from "react";
import { toast } from "react-toastify";
import Table from "../../../../components/elements/table/Table";
import { setTitle } from "../../../../helpers/MetaTag";
import { getPaginationRange, handleFilterChange } from "../../../../helpers";
import DeleteModal from "../../../../components/common/DeleteModal";
import { useDebounce } from "../../../../hooks/useDebounce";
import LimitSelect from "../../../../components/elements/LimitSelect";
import { postDelete, postOrdering, postStatusChange } from "../../../../services/postService";
import Switch from "../../../../components/form/Switch";
import Badge from "../../../../components/elements/Badge";
import Search from "../../../../components/form/Search";
import Button from "../../../../components/form/Button";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { truncateHtmlByWords } from "../../../../util/Flag";
import ManageTestimonialDrawer from "../../../../components/sections/admin/Testimonial/Drawer/ManageTestimonialDrawer";
import TableControls from "../../../../components/elements/TableControls";
import { status_filter } from "../../../../components/constant/constant";
import { roleDelete, roleList, roleStatusChange } from "../../../../services/roleService";
import Drawer from "../../../../components/drawer/Drawer";
import RoleManage from "../../../../components/sections/admin/Role/RoleManage";

const Role = () => {
  setTitle("Role");

  const [selectedItems, setSelectedItems] = useState([]);
  const [modalOpen, setModalOpen] = useState({ isOpen: false, data: null });
  const [keyword, setKeyword] = useState("");
  const search = useDebounce(keyword, 300);
  const queryClient = useQueryClient();
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    data: null,
  });
  const [filters, setFilters] = useState({
    limit: 10,
    page: 1,
    sortQuery: "ordering",
  });

  const { data, error, isLoading, updateData, isFetching, refetch } = useQuery({
    queryKey: ["role", filters, search],
    queryFn: async () => {
      const res = await roleList({
        ...filters,
        ...(search && { keyword: search }),
      });
      if (res?.status !== 200) {
        toast.warning(res?.message);
      }
      return res?.docs;
    },
  });

  //   Testimonial status change handler =================>
  const { mutate: statusChangeHandler, isPending: statusChangeInPending } = useMutation({
    mutationFn: async ({ id, status }) => {
      queryClient.setQueryData(["role", filters, search], (state) => {
        if (!state) return;

        const updatedData = state?.data?.map((item) =>
          item?._id === id
            ? {
                ...item,
                status: item.status === "active" ? "inactive" : "active",
              }
            : item
        );

        return { ...state, data: updatedData };
      });
      const res = await roleStatusChange({ ids: [id], status });

      return { res, payload: { id, status } };
    },

    onSuccess: ({ res, payload }) => {
      if (res?.status === 200) {
        toast.success("Role status change successfully.");
      } else {
        queryClient.setQueryData(["role", filters, search], (state) => {
          if (!state) return;

          const revertedData = state?.data?.map((item) =>
            item?._id === payload.id
              ? {
                  ...item,
                  status: item.status === "active" ? "inactive" : "active",
                }
              : item
          );

          return { ...state, data: revertedData };
        });

        toast.error(res?.message || "Failed to update status.");
      }
    },

    onError: (error) => {
      toast.error(error?.message || "Something went wrong while updating status.");
      console.error("Status update error:", error);
    },
  });

  const tableHeadData = [
    {
      _id: 1,
      name: "Name",
      align: "left",
    },
    {
      _id: 2,
      name: "Code",
      align: "left",
    },
    {
      _id: 4,
      name: "Status",
      align: "left",
    },
    {
      _id: 5,
      name: "Action",
      align: "left",
    },
  ];

  // Table Body Data =====================>
  const tableData = data?.data?.map((item) => {
    return {
      _id: item?._id,
      rowData: [
        {
          _id: 1,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: item?.name || "-",
        },
        {
          _id: 2,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data:  item?.code || "-" ,
        },
        {
          _id: 4,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: (
            <div
              className="flex gap-2 items-center cursor-pointer"
              onClick={() => {
                statusChangeHandler({
                  id: item?._id,
                  status: item?.status === "active" ? "inactive" : "active",
                });
              }}>
              <Badge
                type={item?.status === "active" ? "success" : "danger"}
                label={item?.status === "active" ? "Active" : "Inactive"}
              />
            </div>
          ),
        },
        {
          _id: 5,
          type: "action",
          align: "left",
          cellClass: "w-[5%]",
          actionData: [
            // {
            //   _id: 1,
            //   name: "Edit",
            //   icon: "fa-regular fa-pen-to-square",
            //   onClick: () => {
            //     setModalOpen({ isOpen: true, data: item });
            //   },
            //   standout: false,
            // },

            {
              _id: 2,
              name: "Delete",
              icon: "fa-regular fa-trash-can",
              standout: true,
              onClick: () => {
                setDeleteModal({
                  isOpen: true,
                  data: item,
                });
              },
            },
          ],
        },
      ],
    };
  });

  // add or edit Testimonial =================>
  function testimonialManageResponse(res) {
    if (res?.status === 200 || res?.status === 201) {
      if (res?.status === 201) {
        if (filters?.page === 1) {
          queryClient.setQueryData(["testimonial", filters, search], (state) => {
            if (state) {
              let stateData = JSON.parse(JSON.stringify(state?.data));
              stateData = [...stateData, res?.data];
              return { ...state, data: stateData };
            }
          });
          if (data?.metadata?.totalDocs > filters?.limit) {
            queryClient.invalidateQueries(["testimonial", filters, search]);
          }
        } else {
          setFilters((pre) => ({ ...pre, page: 1 }));
        }
      }
      if (res?.status === 200) {
        queryClient.setQueryData(["testimonial", filters, search], (state) => {
          if (state) {
            let stateData = JSON.parse(JSON.stringify(state?.data));
            stateData = stateData?.map((item) => (item?._id === res?.data?._id ? { ...item, ...res?.data } : item));
            return { ...state, data: stateData };
          }
        });
        if (data?.metadata?.totalDocs > filters?.limit) {
          queryClient.invalidateQueries(["testimonial", filters, search]);
        }
      }
      setModalOpen({ isOpen: false, data: null });
    } else {
      toast.warning(res?.message || res?.error);
    }
  }

  //   Testimonial delete handler =================>
  const { mutate: deleteHandler, isPending: deletePending } = useMutation({
    mutationFn: async (payload) => {
      const res = await roleDelete({
        ids: Array.isArray(payload) ? payload : [payload],
      });
      return { res, payload: Array.isArray(payload) ? payload : [payload] };
    },
    onSuccess: ({ res, payload }) => {
      if (res?.status === 200) {
        setDeleteModal({ isOpen: false, data: null });
        setSelectedItems([]);
        queryClient.setQueryData(["role", filters, search], (state) => {
          if (state) {
            let stateData = JSON.parse(JSON.stringify(state?.data));
            stateData = (stateData || [])?.filter((item) => !payload?.includes(item?._id));
            return { ...state, data: stateData };
          }
        });

        if (data?.metadata?.totalDocs > filters?.limit) {
          queryClient.invalidateQueries(["role", filters, search]);
        }
      } else {
        toast.warning(res?.message || res?.error);
      }
    },
    onError: (error) => {
      console.log(error);
    },
  });

  return (
    <div className="space-y-4 w-full flex flex-col pb-10">
      <div className="space-y-8">
        <div className="font-bold text-lg xl:text-3xl text-primary">{"Roles Management"}</div>
        <div className="flex items-center gap-3">
          <TableControls
            limit={filters?.limit}
            totalDocs={data?.metadata?.totalDocs}
            onLimitChange={(val) => handleFilterChange("limit", val, setFilters)}
            onSearch={(val) => {
              setKeyword(val);
              handleFilterChange("page", 1, setFilters);
            }}
          />
          {/* <div className="relative ml-auto">
            <Button
              buttonClasses={""}
              buttonLabel={"Add Role"}
              buttonIcon={"fa-regular fa-plus"}
              buttonIconPosition="left"
              buttonLabelClasses={"font-medium !text-sm"}
              buttonFunction={() => setModalOpen({ isOpen: true, data: null })}
            />
          </div> */}
        </div>
      </div>

      <Table
        tableHeadData={tableHeadData}
        tableData={tableData}
        totalDataCount={data?.metadata?.totalDocs}
        isLoder={isLoading}
        pageCount={data?.metadata?.totalPages}
        currentPage={filters?.page}
        onPageChange={(val) => {
          handleFilterChange("page", val, setFilters);
          setSelectedItems([]);
        }}
        handleOrdering={(val) => {
          console.log(val);
        }}
        isFetching={isFetching}
        isOrderingDisabled={true}
        selectedItems={selectedItems}
        setSelectedItems={setSelectedItems}
        tableActionDropdownContainer={"!w-48"}
        showingData={getPaginationRange(filters?.page, filters?.limit, data?.metadata?.totalDocs)}
      />
      {/* <Drawer
        open={modalOpen?.isOpen}
        onClose={(val) => {
          close(val);
        }}
        actualSize={"xl"}
        size={"xl3"}
        title={data?._id ? `Update ${title} Tag` : `Add ${title} Tag`}
        subtitle=""
        drawerBodyClass="p-0 max-h-[calc(100%-90px)]"
      >
        <div className="h-full overflow-y-auto">
          <RoleManage
            getResponse={testimonialManageResponse}
            close={() => setModalOpen({ isOpen: false, data: null })}
            data={modalOpen?.data}
            type={type}
          />
        </div>
      </Drawer> */}
      <DeleteModal
        open={deleteModal?.isOpen}
        loading={deletePending}
        onClose={() => setDeleteModal((pre) => ({ data: null, isOpen: false }))}
        deleteHandler={() => {
          deleteHandler(deleteModal?.data?._id);
        }}
      />
    </div>
  );
};

export default Role;
